import * as amplitude from "@amplitude/analytics-browser";
import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { useHandler } from "@redotech/react-util/hook";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  returnDetailsV2Flag,
  returnDetailsV2RedoUsersFlag,
} from "@redotech/redo-return-core-common/returns-feature-flags";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { useContext, useSyncExternalStore } from "react";
import { PlatformEvents } from "../../platform-events";
import {
  resolveReturnsExperience,
  ReturnsExperienceChoice,
} from "./returns-experience";

const CHOICE_STORAGE_KEY = "redo.returns-experience";
/** Before opt-in only a switch back was stored, under the details page's key. */
const LEGACY_STORAGE_KEY = "redo.return-details-legacy";
const CHOICE_CHANGED = "redo:returns-experience-changed";

/** Falls back to this when storage is unavailable, so a switch still applies to the tab. */
let inMemoryChoice: ReturnsExperienceChoice | undefined;

function readChoice(): ReturnsExperienceChoice | undefined {
  try {
    const stored = localStorage.getItem(CHOICE_STORAGE_KEY);
    if (stored === "new" || stored === "classic") {
      return stored;
    }
    return localStorage.getItem(LEGACY_STORAGE_KEY) === "true"
      ? "classic"
      : undefined;
  } catch {
    return inMemoryChoice;
  }
}

function writeChoice(choice: ReturnsExperienceChoice) {
  inMemoryChoice = choice;
  try {
    localStorage.setItem(CHOICE_STORAGE_KEY, choice);
    // Kept in step so a build that only knows the old key agrees.
    if (choice === "classic") {
      localStorage.setItem(LEGACY_STORAGE_KEY, "true");
    } else {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
  } catch {
    // Private mode or quota exhausted; the in-memory value still applies.
  }
  window.dispatchEvent(new Event(CHOICE_CHANGED));
}

function subscribeToChoice(onChange: () => void) {
  window.addEventListener(CHOICE_CHANGED, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHOICE_CHANGED, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export interface ReturnsExperience {
  /** Whether the user may switch between the new experience and the old one. */
  canSwitch: boolean;
  useNewUi: boolean;
  /** In the rollout, not a Redo user, and hasn't opted in or switched back yet. */
  undecided: boolean;
  switchToLegacy(): void;
  switchToNew(): void;
}

/**
 * The one place that reads the returns experience flags and the user's switch.
 * The flag arrives with the team, so there is no pending state.
 */
export function useReturnsExperience(): ReturnsExperience {
  const team = useContext(TeamContext);
  const user = useContext(UserContext);
  const choice = useSyncExternalStore(subscribeToChoice, readChoice);

  const switchToLegacy = useHandler(() => {
    writeChoice("classic");
    amplitude.track(PlatformEvents.RETURN_DETAILS_EXPERIENCE_SWITCHED, {
      to: "legacy",
    });
  });

  const switchToNew = useHandler(() => {
    writeChoice("new");
    amplitude.track(PlatformEvents.RETURN_DETAILS_EXPERIENCE_SWITCHED, {
      to: "new",
    });
  });

  const { useNewUi, canSwitch, undecided } = resolveReturnsExperience({
    teamInRollout: team?.featureFlags?.[returnDetailsV2Flag.key] === true,
    redoUserInRollout:
      isRedoEmailAddress(user?.email) &&
      team?.featureFlags?.[returnDetailsV2RedoUsersFlag.key] === true,
    choice,
  });

  return { canSwitch, useNewUi, undecided, switchToLegacy, switchToNew };
}
